import React, { useState, useEffect, useCallback } from "react";
import {
  ArrowRight,
  MapPin,
  RefreshCw,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import "./ContactSupport.css";
import heroDevBg from "../assets/assets/hero-developers-bg.jpg";
import { submitContact } from "../utils/api";
import CaptchaBadge from "./CaptchaBadge";

function ContactSupport() {
  const [showMap, setShowMap] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
    captcha: "",
  });
  const [errors, setErrors] = useState({});
  const [captchaCode, setCaptchaCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const googleMapsUrl =
    "https://www.google.com/maps/place/PINKMOON+Technologies+Private+Limited/@16.4883,80.6625,17z";

  const generateCaptcha = useCallback(() => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const handleMapClick = (e) => {
    e.preventDefault();
    setShowMap(!showMap);
    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  };

  const handleRefreshCaptcha = () => {
    generateCaptcha();
    setFormData((prev) => ({ ...prev, captcha: "" }));
    setErrors((prev) => ({ ...prev, captcha: "" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalVal = value;

    if (name === "phone") {
      finalVal = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({ ...prev, [name]: finalVal }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (serverError) {
      setServerError("");
    }
  };

  const validate = () => {
    const nextErrors = {};
    const nameRegex = /^[A-Za-z\s.'-]+$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\d{10}$/;

    const trimmedName = formData.fullName.trim();
    if (!trimmedName) {
      nextErrors.fullName = "Name and surname is required.";
    } else if (!nameRegex.test(trimmedName)) {
      nextErrors.fullName = "Name can only contain letters and spaces.";
    } else if (trimmedName.length < 2) {
      nextErrors.fullName = "Name must be at least 2 characters.";
    }

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      nextErrors.email = "Email address is required.";
    } else if (!emailRegex.test(trimmedEmail)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    const trimmedPhone = formData.phone.trim();
    if (!trimmedPhone) {
      nextErrors.phone = "Phone number is required.";
    } else if (!phoneRegex.test(trimmedPhone)) {
      nextErrors.phone = "Phone number must be exactly 10 digits.";
    }

    const trimmedMessage = formData.message.trim();
    if (!trimmedMessage) {
      nextErrors.message = "Message content is required.";
    } else if (trimmedMessage.length < 5) {
      nextErrors.message = "Message must be at least 5 characters.";
    }

    const trimmedCaptcha = formData.captcha.trim();
    if (!trimmedCaptcha) {
      nextErrors.captcha = "CAPTCHA is required.";
    } else if (trimmedCaptcha.toUpperCase() !== captchaCode.toUpperCase()) {
      nextErrors.captcha = "Invalid CAPTCHA code. Please try again.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
      };

      await submitContact(payload);

      setIsSubmitted(true);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        message: "",
        captcha: "",
      });
      setErrors({});
      generateCaptcha();
    } catch (error) {
      console.error("Failed to submit contact form:", error);
      setServerError(error.message || "Failed to send message. Please try again later.");
      handleRefreshCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setIsSubmitted(false);
    setServerError("");
    setErrors({});
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      message: "",
      captcha: "",
    });
    generateCaptcha();
  };

  const whyChooseCards = [
    {
      title: "Expert Support",
      desc: "Experienced technology professionals",
    },
    {
      title: "Quick Response",
      desc: "We value your time",
    },
    {
      title: "Customer First",
      desc: "Your success is our priority",
    },
    {
      title: "Trusted & Secure",
      desc: "Your data is safe with us",
    },
  ];

  return (
    <section className="contact-support-section-v2" id="contact-support">
      <div className="contact-support-container-v2">
        {/* Main Hero Card Composition */}
        <div className="contact-support-hero-card">
          {/* Background Image Layer */}
          <div className="cs-hero-bg-layer">
            <img src={heroDevBg} alt="Support Backdrop" className="cs-hero-bg-img" />
            <div className="cs-hero-gradient-overlay"></div>
          </div>

          {/* Left Column: Heading, Subtitle, 2x2 Why Choose Us Cards, and Button */}
          <div className="cs-hero-content-left">
            <div className="cs-badge-pill">WE'RE HERE TO HELP</div>
            <h2 className="cs-hero-title">Get in touch with us</h2>
            <p className="cs-hero-desc">
              Our support team is here to help you with projects, solutions,
              technologies, reports and all your business needs.
            </p>

            {/* Why Choose Us Heading */}
            <h3 className="cs-why-heading-top">Why Choose Us?</h3>

            {/* 2x2 Grid of Why Choose Us Cards (Matching Image 1 Layout) */}
            <div className="cs-why-cards-grid">
              {whyChooseCards.map((card, idx) => (
                <div key={idx} className="cs-why-feature-card">
                  <h4 className="cs-why-card-title">{card.title}</h4>
                  <p className="cs-why-card-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Contact Form (Matching Image 3 Design) */}
          <div className="cs-form-card-right">
            <h3 className="cs-form-title">Send Us a Message</h3>
            <p className="cs-form-subtitle">
              Share your query and we'll respond within 24 hours.
            </p>

            {isSubmitted ? (
              <div className="cs-form-success-box">
                <CheckCircle2 size={46} className="cs-success-icon" />
                <h4>Thank You!</h4>
                <p>Your message has been sent successfully. Our team will get back to you shortly.</p>
                <button
                  type="button"
                  className="cs-reset-btn"
                  onClick={handleResetForm}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="cs-inquiry-form" noValidate>
                {serverError && (
                  <div className="cs-form-alert-error" role="alert">
                    {serverError}
                  </div>
                )}

                <div className="cs-form-group">
                  <label htmlFor="cs-fullName">NAME AND SURNAME *</label>
                  <input
                    id="cs-fullName"
                    type="text"
                    name="fullName"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={errors.fullName ? "cs-input-error" : ""}
                  />
                  {errors.fullName && (
                    <span className="cs-form-field-error">{errors.fullName}</span>
                  )}
                </div>

                <div className="cs-form-group">
                  <label htmlFor="cs-email">EMAIL *</label>
                  <input
                    id="cs-email"
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? "cs-input-error" : ""}
                  />
                  {errors.email && (
                    <span className="cs-form-field-error">{errors.email}</span>
                  )}
                </div>

                <div className="cs-form-group">
                  <label htmlFor="cs-phone">PHONE NUMBER *</label>
                  <input
                    id="cs-phone"
                    type="tel"
                    name="phone"
                    maxLength={10}
                    placeholder="Enter your 10-digit phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? "cs-input-error" : ""}
                  />
                  {errors.phone && (
                    <span className="cs-form-field-error">{errors.phone}</span>
                  )}
                </div>

                <div className="cs-form-group">
                  <label htmlFor="cs-message">MESSAGE CONTENT *</label>
                  <textarea
                    id="cs-message"
                    name="message"
                    rows="3"
                    placeholder="Write your message here..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className={errors.message ? "cs-input-error" : ""}
                  ></textarea>
                  {errors.message && (
                    <span className="cs-form-field-error">{errors.message}</span>
                  )}
                </div>

                {/* Captcha Verification Group */}
                <div className="cs-form-group cs-captcha-group">
                  <div className="cs-captcha-row">
                    <div className="cs-captcha-box" aria-label="CAPTCHA code">
                      <CaptchaBadge
                        code={captchaCode}
                        width={105}
                        height={34}
                        bgColor="#ffffff"
                        textColor="#0f172a"
                        fontSize={18}
                      />
                      <button
                        type="button"
                        className="cs-captcha-refresh-btn"
                        onClick={handleRefreshCaptcha}
                        title="Refresh CAPTCHA"
                        aria-label="Refresh CAPTCHA"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                    <input
                      type="text"
                      name="captcha"
                      placeholder="CAPTCHA"
                      className={`cs-captcha-input ${errors.captcha ? "cs-input-error" : ""}`}
                      value={formData.captcha}
                      onChange={handleInputChange}
                      onPaste={(e) => e.preventDefault()}
                      autoComplete="off"
                      maxLength={6}
                      aria-label="CAPTCHA Input"
                    />
                  </div>
                  {errors.captcha && (
                    <span className="cs-form-field-error">{errors.captcha}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="cs-submit-btn"
                  disabled={isSubmitting}
                >
                  <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
                  {isSubmitting ? (
                    <Loader2 size={16} className="cs-spinner-icon" />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Full Width Dark Navy Location Banner Below */}
      <div className="cs-location-banner-dark">
        <div className="cs-location-banner-inner">
          <h2 className="cs-location-banner-title">
            Vijayawada, Andhra Pradesh
          </h2>
          <p className="cs-location-banner-desc">
            Delivering next-generation IT consulting & digital transformation solutions Globally.
          </p>

          <a
            href="https://www.google.com/maps/search/?api=1&query=PINKMOON+Technologies+Private+Limited,+Kanuru,+Vijayawada,+Andhra+Pradesh"
            target="_blank"
            rel="noopener noreferrer"
            className="cs-location-map-btn"
          >
            <MapPin size={18} />
            <span>VIEW OFFICE ON GOOGLE MAPS</span>
            <ArrowRight size={16} />
          </a>

          {/* Interactive Google Maps Embed Container */}
          {showMap && (
            <div className="cs-google-maps-embed-wrap">
              <iframe
                title="Pinkmoon Technologies Google Map"
                src="https://maps.google.com/maps?q=11-182,%20First%20Floor,%20Kunapareddy%20Srimannarayana%20St,%20near%20Time%20Hospital,%20Ashok%20Nagar,%20Kanuru,%20Vijayawada,%20Andhra%20Pradesh%20520007&t=&z=16&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="380"
                style={{ border: 0, borderRadius: "16px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ContactSupport;
